import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Button component with exact Hyperkit styling
 *
 * Hyperkit Specs:
 * - Border Radius: 8px (rounded-lg)
 * - Heights: sm=24px, md=32px, lg=36px
 * - Focus: 4px ring at 10% opacity
 * - Transitions: 100ms hover, 150ms focus
 * - Primary: grey-950 bg, white text, inner border glow
 */
const buttonVariants = cva(
  // Base: 8px radius, 14px/500 font, smooth transitions, focus ring
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-100 ease-out focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-grey-950/10 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] active:duration-50',
  {
    variants: {
      variant: {
        // Primary: Dark bg with inner white glow border
        default:
          'bg-grey-950 text-white border border-white/30 shadow-button-primary hover:bg-grey-800 active:bg-grey-900 dark:bg-grey-50 dark:text-grey-950 dark:border-grey-950/10 dark:hover:bg-grey-200',
        // Secondary: White bg with grey border
        secondary:
          'bg-white text-grey-950 border border-grey-200 shadow-sm hover:bg-grey-50 active:bg-grey-100 dark:bg-grey-800 dark:text-grey-50 dark:border-grey-700 dark:hover:bg-grey-700',
        // Tertiary: Transparent, no border
        tertiary:
          'bg-transparent text-grey-950 hover:bg-grey-100 active:bg-grey-200 dark:text-grey-50 dark:hover:bg-grey-800',
        // Destructive: Red solid
        destructive:
          'bg-red-500 text-white hover:bg-red-600 active:bg-red-700 focus-visible:ring-red-500/20',
        // Danger: Transparent with red border
        danger:
          'bg-transparent text-red-500 border border-red-500 hover:bg-red-50 active:bg-red-100 dark:hover:bg-red-950/30 focus-visible:ring-red-500/20',
        // Dashed: White bg with dashed border
        dashed:
          'bg-white text-grey-950 border border-dashed border-grey-200 hover:bg-grey-50 hover:border-grey-300 dark:bg-grey-900 dark:text-grey-50 dark:border-grey-700',
        // Plain: Minimal, muted text
        plain:
          'bg-transparent text-grey-500 hover:text-grey-700 dark:hover:text-grey-300',
        // Ghost: Like tertiary but more subtle
        ghost:
          'hover:bg-grey-100 hover:text-grey-900 dark:hover:bg-grey-800 dark:hover:text-grey-50',
        // Link: Text link style
        link: 'text-blue-500 underline-offset-4 hover:underline',
        // Success: Green solid
        success:
          'bg-green-500 text-white hover:bg-green-600 active:bg-green-700 focus-visible:ring-green-500/20',
        // Warning: Orange solid
        warning:
          'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700 focus-visible:ring-orange-500/20',
      },
      size: {
        // sm: 24px height, 12px padding, 14px font
        sm: 'h-6 px-3 py-1 text-sm',
        // md (default): 32px height, 12px padding
        default: 'h-8 px-3 py-1.5',
        // lg: 36px height, 16px padding
        lg: 'h-9 px-4 py-2',
        // icon: Square buttons
        icon: 'h-8 w-8',
        'icon-sm': 'h-6 w-6',
        'icon-lg': 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
